<script setup>
import ga4 from '#common/utils/ga4'
import { pushEvent } from '#common/utils/user'

const props = defineProps({
  menu: {
    type: Object,
    default: () => ({ items: [], text: 'default' }),
  },
  root: {
    type: Boolean,
    default: true,
  },
  hierarchy: {
    type: String,
    default: '',
  },
})
const { $auth, $store } = useNuxtApp()

const layout = useLayout()
onUpdated(() => {
  setTimeout(() => {
    layout.showDesktopMenus = true
  }, 1000)
})
function hasMeta(item, meta) {
  return item.meta && item.meta.includes(meta)
}
function requiresLogin(item) {
  return !$auth.loggedIn && hasMeta(item, 'requiresLogin')
}
function showLogin(item) {
  return !$auth.loggedIn && hasMeta(item, 'showLogin')
}

function buttonClasses(item) {
  if (!item.buttonClasses) return ''
  return item.buttonClasses
}
function logout() {
  layout.showMobileMenu = false
  $auth.logoutUser()
}
function login() {
  layout.showMobileMenu = false
  $auth.openAuthModal()
}
function ulClasses() {
  if (!props.root) {
    return 'menu-dropdown'
  } else {
    return ''
  }
}
function liClasses(item) {
  return {
    'has-submenu': !!item.items,
    simple: hasMeta(item, 'simple'),
    'mobile-only': hasMeta(item, 'mobile-only'),
    'start-group': hasMeta(item, 'start-group'),
    'more-dup': hasMeta(item, 'more-dup'),
    more: hasMeta(item, 'more'),
  }
}
function linkClasses(item) {
  return {
    all: hasMeta(item, 'all'),
    'en-primeur': hasMeta(item, 'en-primeur'),
  }
}
function divWrapperClasses(item) {
  return {
    'menu-mega': hasMeta(item, 'menu-mega'),
    inline: hasMeta(item, 'inline'),
  }
}
function linkClicked(item) {
  const $route = useRoute()
  pushEvent(
    {
      metric_name: 'Global Nav Click',
      url_path: $route.fullPath,
      extra_data: {
        menu_name: item.text,
        parent_menu_name: props.hierarchy,
        target_url: item.url,
      },
    },
    $store,
  )
  ga4.track('global_nav_click', {
    from: $route.fullPath,
    name: item.text,
    parent_name: props.hierarchy,
    path: item.url,
  })
  hideSubmenus()
}
function hideSubmenus() {
  layout.showDesktopMenus = false
}
function fullHierarchy(item) {
  return (props.hierarchy ? props.hierarchy + ' / ' : '') + item.text
}
</script>

<template>
  <ul :class="ulClasses()">
    <li
      v-for="menuItem of menu.items"
      :key="menuItem.text"
      :class="liClasses(menuItem)"
    >
      <!-- if login required, display Sign In buttons instead -->
      <template v-if="showLogin(menuItem)">
        <button :class="'underline'" @click="login()">Sign Up / Log In</button>
      </template>
      <template v-else-if="requiresLogin(menuItem)">
        <span><!-- intentionally blank --></span>
      </template>
      <template v-else-if="hasMeta(menuItem, 'cellar')">
        <MyCellarBadge v-if="$auth.loggedIn" />
      </template>
      <template v-else-if="hasMeta(menuItem, 'orders')">
        <MyOrdersBadge v-if="$auth.loggedIn" />
      </template>
      <!-- special handler for doesLogout buttons -->
      <button
        v-else-if="hasMeta(menuItem, 'doesLogout')"
        class="underline"
        @click="logout()"
      >
        {{ menuItem.text }}
      </button>
      <!-- If root menu has sub-items, this is a trigger -->
      <template v-else-if="menu.text === 'root' && !!menuItem.items">
        <button :class="buttonClasses(menuItem)">
          <span v-if="hasMeta(menuItem, 'hideTitle')" class="sr-only">
            {{ menuItem.text }}
          </span>
          <template v-else>
            {{ menuItem.text }}
          </template>
        </button>
        <div
          v-if="layout.showDesktopMenus"
          :class="divWrapperClasses(menuItem)"
        >
          <DesktopMenu
            :menu="menuItem"
            :root="false"
            :hierarchy="fullHierarchy(menuItem)"
          />
        </div>
      </template>
      <!-- If non-root menu has sub-items, this is a category -->
      <template v-else-if="!!menuItem.items">
        <div class="show-for-large-up group-header">
          {{ menuItem.text }}
        </div>
        <DesktopMenu
          :menu="menuItem"
          :root="false"
          :hierarchy="fullHierarchy(menuItem)"
        />
      </template>
      <!-- If link starts with '/', this is an internal (nuxt) link -->
      <nuxt-link
        v-else-if="menuItem.url && menuItem.url.startsWith('/')"
        :to="menuItem.url"
        :class="linkClasses(menuItem)"
        event=""
        no-prefetch
        @click="linkClicked(menuItem)"
      >
        {{ menuItem.text }}
      </nuxt-link>
      <!-- Otherwise, this is an external link -->
      <a v-else :href="menuItem.url" :class="linkClasses(menuItem)">{{
        menuItem.text
      }}</a>
    </li>
  </ul>
</template>
